/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Icon } from '@bwoty-web/ui-kit';
import { UI_CHEVRON_DOWN, UI_CHEVRON_UP } from '@bwoty-web/ui-kit/icons';

import ItemHeading from './itemHeading';
import queryHelper from '../../../utils/url/queryHelper';

import './treeList.scss';

const TreeList = ({ tree, selectedId }) => {
  const path = TreeList.getPath(tree.destinations, selectedId) || [];
  const [level2, level3] = path;
  const [level1ExpandedIds, setLevel1ExpandedIds] = useState([tree?.id]);
  const [level2ExpandedIds, setLevel2ExpandedIds] = useState(
    level2 ? [level2] : [],
  );
  const [level3ExpandedIds, setLevel3ExpandedIds] = useState(
    level3 ? [level3] : [],
  );

  const handleExpand = (id, level, isExpanded) => {
    switch (level) {
      case 1:
        setLevel1ExpandedIds(isExpanded ? [] : [id]);
        break;
      case 2: {
        const index = level2ExpandedIds.indexOf(id);
        if (isExpanded && index !== -1) {
          setLevel2ExpandedIds(
            level2ExpandedIds.filter((expandedId) => {
              return expandedId !== id;
            }),
          );
        } else if (!isExpanded && index === -1) {
          setLevel2ExpandedIds([...level2ExpandedIds, id]);
        }
        break;
      }
      case 3: {
        const index = level3ExpandedIds.indexOf(id);
        if (isExpanded && index !== -1) {
          setLevel3ExpandedIds(
            level3ExpandedIds.filter((expandedId) => {
              return expandedId !== id;
            }),
          );
        } else if (!isExpanded && index === -1) {
          setLevel3ExpandedIds([...level3ExpandedIds, id]);
        }
        break;
      }
      default:
        break;
    }
  };

  const renderTreeItem = (item, level, parentId) => {
    const { id } = item;
    let isExpanded = [];
    switch (level) {
      case 1:
        isExpanded = (level1ExpandedIds || []).includes(id);
        break;
      case 2:
        isExpanded = (level2ExpandedIds || []).includes(id);
        break;
      case 3:
        isExpanded = (level3ExpandedIds || []).includes(id);
        break;
      default:
        break;
    }

    if (TreeList.ignoreItem(item)) {
      return item.destinations.map((subItem) =>
        renderTreeItem(subItem, level, parentId),
      );
    }

    const treeItemClasses = classNames(
      'gw-tree-list-item',
      `gw-tree-list-item--level-${level}`,
      { 'gw-tree-list-item--expanded': isExpanded },
    );

    if (item.destinations) {
      return (
        <div id={`id_${id}`} className={treeItemClasses} key={id}>
          <div
            className="gw-tree-list-item__heading-container"
            onClick={() => handleExpand(id, level, isExpanded)}
          >
            <ItemHeading
              heading={item.name}
              url={item.url}
              level={level}
              onClick={(event) =>
                TreeList.updateUrlWithIdAndNavigate(event, id, item.url)
              }
            />
            <button className="gw-tree-list-item__expand-button" type="button">
              <Icon
                path={isExpanded ? UI_CHEVRON_UP : UI_CHEVRON_DOWN}
                size="xs"
                alt="Expand or shrink list"
              />
            </button>
          </div>
          <div className="gw-tree-list-item__children">
            {item.destinations.map((subItem) =>
              renderTreeItem(subItem, level + 1, id),
            )}
          </div>
        </div>
      );
    }

    return (
      <div id={`id_${id}`} className={treeItemClasses} key={id}>
        <div className="gw-tree-list-item__heading-container gw-tree-list-item__no-children">
          <ItemHeading
            heading={item.name}
            url={item.url}
            level={level}
            onClick={(event) =>
              TreeList.updateUrlWithIdAndNavigate(event, parentId, item.url)
            }
          />
        </div>
      </div>
    );
  };

  return <div className="gw-tree-list">{renderTreeItem(tree, 2, null)}</div>;
};

TreeList.updateUrlWithIdAndNavigate = (event, id, navigateUrl) => {
  // Prevent redirection to url
  event.preventDefault();
  // Stop event bubbling to prevent expanding the country/area
  event.stopPropagation();
  // Update query with selected itemId
  queryHelper.updateQueryWithValues({ id });
  // Navigate to url
  window.location.href = navigateUrl;
};

TreeList.getPath = (tree, selectedId) => {
  if (selectedId == null) {
    return null;
  }
  for (let index = 0; index < tree?.length; index += 1) {
    const { destinations, id } = tree[index];
    if (id === selectedId) {
      return [id];
    }

    if (destinations) {
      const path = TreeList.getPath(destinations, selectedId);
      if (path) {
        path.unshift(id);
        return path;
      }
    }
  }
  return null;
};

TreeList.ignoreItem = (item) => {
  return item.url === null && item.type === 'AREA' && item.destinations;
};

TreeList.propTypes = {
  tree: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    url: PropTypes.string,
    destinations: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  selectedId: PropTypes.string,
};

TreeList.defaultProps = {
  selectedId: null,
};

export default TreeList;
